import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Megaphone, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useCreateCampaign } from "@/hooks/useCampaigns";
import { useCompanies, useCompanyMembers } from "@/hooks/useCompanies";
import { calcularConversao } from "@/lib/conversionCalculator";
import type { RemuneracaoModelo } from "@/types/api";

const EXAMPLE_VALOR = 100;

function fmtBrl(n: number) {
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtValor(n: number, usaPontos: boolean) {
  return usaPontos ? `${Math.round(n)} pts` : `R$ ${fmtBrl(n)}`;
}

export default function NewCampaign() {
  const { companyId: paramCompanyId } = useParams();
  const [name, setName] = useState("");
  const [companyId, setCompanyId] = useState(paramCompanyId || "");
  const [influenciadorId, setInfluenciadorId] = useState("");
  const [modelo, setModelo] = useState<"fixo" | "percentual">("fixo");
  const [valor, setValor] = useState("");
  const [desconto, setDesconto] = useState("");
  const [usaPontos, setUsaPontos] = useState(false);

  const navigate = useNavigate();
  const createMutation = useCreateCampaign();
  const { data: companies, isLoading: loadingCompanies } = useCompanies();
  const { data: members, isLoading: loadingMembers } = useCompanyMembers(companyId);

  const influencers = members?.items?.filter((m) => m.role === "influenciador") ?? [];

  const preview = (() => {
    const v = parseFloat(valor);
    const d = desconto && parseFloat(desconto) > 0 ? parseFloat(desconto) : null;
    if (!valor || isNaN(v) || v <= 0) return null;
    return calcularConversao(EXAMPLE_VALOR, modelo as RemuneracaoModelo, v, d);
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !companyId || !influenciadorId || !valor) {
      toast.error("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    createMutation.mutate(
      {
        name,
        company_id: parseInt(companyId),
        influenciador_id: parseInt(influenciadorId),
        remuneracao_modelo: modelo,
        remuneracao_valor: usaPontos ? Math.round(parseFloat(valor)) : parseFloat(valor),
        desconto_pct: desconto ? parseFloat(desconto) : undefined,
        usa_pontos: usaPontos,
      },
      {
        onSuccess: () => {
          toast.success("Campanha criada com sucesso!");
          navigate(paramCompanyId ? `/empresas/${paramCompanyId}` : "/campanhas");
        },
        onError: (error: unknown) => {
          const apiError = error as { response?: { data?: { detail?: unknown } } };
          const detail = apiError.response?.data?.detail;
          const message = Array.isArray(detail)
            ? detail.map((e: { msg?: string }) => e.msg).filter(Boolean).join(", ")
            : typeof detail === "string" ? detail : "Erro ao criar campanha.";
          toast.error(message || "Erro ao criar campanha.");
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        className="gap-2 -ml-2 text-muted-foreground"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </Button>

      <div className="max-w-2xl mx-auto space-y-4">
        <Card>
          <form onSubmit={handleSubmit}>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                  <Megaphone className="h-5 w-5" />
                </div>
                <CardTitle>Nova Campanha</CardTitle>
              </div>
              <CardDescription>
                Configure uma nova campanha de indicação para um indicador.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome da Campanha</Label>
                <Input
                  id="name"
                  placeholder="Ex: Campanha de Verão 2026"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company">Empresa</Label>
                  <select
                    id="company"
                    value={companyId}
                    onChange={(e) => { setCompanyId(e.target.value); setInfluenciadorId(""); }}
                    disabled={!!paramCompanyId || loadingCompanies}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Selecione a empresa</option>
                    {companies?.items?.map((c) => (
                      <option key={c.id} value={c.id.toString()}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="influencer">Indicador</Label>
                  <select
                    id="influencer"
                    value={influenciadorId}
                    onChange={(e) => setInfluenciadorId(e.target.value)}
                    disabled={!companyId || loadingMembers}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">
                      {!companyId ? "Selecione a empresa primeiro" : "Selecione o indicador"}
                    </option>
                    {influencers.map((m) => (
                      <option key={m.usuario_id} value={m.usuario_id.toString()}>{m.email}</option>
                    ))}
                    {influencers.length === 0 && companyId && !loadingMembers && (
                      <option disabled value="">Nenhum indicador cadastrado</option>
                    )}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2 py-1">
                <input
                  id="usa-pontos"
                  type="checkbox"
                  checked={usaPontos}
                  onChange={(e) => {
                    setUsaPontos(e.target.checked);
                    setValor("");
                  }}
                  className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
                />
                <Label htmlFor="usa-pontos" className="cursor-pointer font-normal">
                  Usar pontos em vez de dinheiro (R$)
                </Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="modelo">Modelo de Remuneração</Label>
                  <select
                    id="modelo"
                    value={modelo}
                    onChange={(e) => setModelo(e.target.value as "fixo" | "percentual")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="fixo">{usaPontos ? "Fixo (pts)" : "Fixo (R$)"}</option>
                    <option value="percentual">Percentual (%)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="valor">
                    {modelo === "percentual" ? "Remuneração (%)" : usaPontos ? "Remuneração (pts)" : "Remuneração (R$)"}
                  </Label>
                  <Input
                    id="valor"
                    type="number"
                    step={usaPontos ? "1" : "0.01"}
                    min={usaPontos ? "1" : "0.01"}
                    placeholder={usaPontos ? "0" : "0.00"}
                    value={valor}
                    onChange={(e) => setValor(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="desconto">Desconto para o Cliente (%) — opcional</Label>
                <Input
                  id="desconto"
                  type="number"
                  step="any"
                  min="0"
                  max="100"
                  placeholder="Ex: 10"
                  value={desconto}
                  onChange={(e) => setDesconto(e.target.value)}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-3 border-t pt-6">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createMutation.isPending} className="min-w-[120px]">
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Criando...
                  </>
                ) : (
                  "Criar Campanha"
                )}
              </Button>
            </CardFooter>
          </form>
        </Card>

        {preview && (
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Prévia — exemplo com venda de {fmtValor(EXAMPLE_VALOR, usaPontos)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor bruto</span>
                <span>{fmtValor(EXAMPLE_VALOR, usaPontos)}</span>
              </div>
              {preview.descontoValor > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Desconto cliente ({desconto}%)</span>
                  <span>- {fmtValor(preview.descontoValor, usaPontos)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium">
                <span>Valor cobrado do cliente</span>
                <span>{fmtValor(preview.valorLiquido, usaPontos)}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-muted-foreground">
                <span>
                  Comissão indicador
                  {modelo === "percentual" ? ` (${valor}%)` : " (fixo)"}
                </span>
                <span>{fmtValor(preview.remuneracaoValor, usaPontos)}</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
